import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { CircleForm } from "../allComponents/AllComponents";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../redux/store/store";
import {
  selectIsLoading,
  selectedCurrentCircleInfo,
} from "../../redux/selectors/circleSelector";
import {
  EmptyConfigurationType,
  IUpdateCircle,
} from "../../utility/types/circleType";
import { selectCurrentUser } from "../../redux/selectors/userSelector";
import {
  fetchCircleByLeader,
  updateCircle,
} from "../../redux/actions/circleAction";
import { BootstrapDialog } from "../modal/notification-modal/styles";
import { colors, fonts } from "../../utility/types/constant";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";

interface UpdateCircleProps {
  open: boolean;
  handleClose: () => void;
  circleId: string;
}

const UpdateCircle = (props: UpdateCircleProps) => {
  const { open, handleClose, circleId } = props;

  const userData = useSelector(selectCurrentUser);

  const dispatch = useDispatch<AppDispatch>();

  const circleInfoArray = useSelector(selectedCurrentCircleInfo);

  const isLoading = useSelector(selectIsLoading);
  // find specific circle to update.
  const circleInfo = circleInfoArray.find((c) => c._id === circleId);

  const [successAlert, setSuccessAlert] = useState(false);

  const [errorAlert, setErrorAlert] = useState("");

  const [imagePreview, setImagePreview] = useState("");

  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);

  const [updateSuccess, setUpdateSuccess] = useState(false);

  const valObj = {
    circleName: circleInfo?.circleName || "",
    circleImage: circleInfo?.circleImage || undefined,
    description: circleInfo?.description || "",
    leader: circleInfo?.leader?._id ? circleInfo.leader._id.toString() : "",
    membersCount: circleInfo?.membersCount || "",
    // memberId: "",
    // contribution:
    //   circleInfo?.contributions.map((contribution) => ({
    //     _id: contribution._id,
    //     memberId: contribution.memberId,
    //     contributionAmount: contribution.contributionAmount,
    //     maxContributionTime: contribution.maxContributionTime,
    //     contributionPeriod: contribution.contributionPeriod,
    //     contributionDays: contribution.contributionDays,
    //   })) || [],

    configuration: circleInfo?.configuration || EmptyConfigurationType,

    payOut: circleInfo?.payOut || { payOutPeriod: "", payOutDay: "" },
    userId: "",
  };

  const [initialValues, setInitialValues] = useState<IUpdateCircle>(valObj);

  useEffect(() => {
    if (circleInfo) {
      setInitialValues({
        circleName: circleInfo.circleName || "",
        circleImage: circleInfo.circleImage || "",
        description: circleInfo.description || "",
        leader: circleInfo.leader?._id.toString() || "",
        membersCount: circleInfo.membersCount || "",
        // contribution:
        //   circleInfo.contributions?.map((contribution) => ({
        //     _id: contribution._id,
        //     memberId: contribution.memberId,
        //     contributionAmount: contribution?.contributionAmount,
        //     maxContributionTime: contribution?.maxContributionTime,
        //     contributionPeriod: contribution?.contributionPeriod,
        //     contributionDays: contribution?.contributionDays,
        //   })) || [],
        configuration: circleInfo?.configuration || {
          contributionAmount: 0,
          maxContributionTime: "",
          contributionPeriod: "",
          contributionFrequency: 0,
          contributionDays: [],
          payOutPeriod: "Monthly",
          payOutDay: circleInfo?.configuration?.payOutDay || "",
        },

        // payOut: circleInfo.payOut || {
        //   payOutPeriod: "Monthly",
        //   payOutDay: circleInfo?.configuration?.payOutDay || "",
        // },
      });
    }
  }, [circleInfo, userData?._id]);

  const handleSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Check if any file is selected
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      const filePreview = URL.createObjectURL(file);

      setImagePreview(filePreview);
      setSelectedFile(file);
    }
  };

  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  });

  // const handleSubmit = async (values: IUpdateCircle) => {
  //   try {
  //     const formData = new FormData();

  //     // Append text fields and JSON.stringify array fields
  //     Object.entries(values).forEach(([key, value]) => {
  //       if (Array.isArray(value)) {
  //         // Append stringified version of array or object
  //         formData.append(key, JSON.stringify(value));
  //       } else if (value instanceof File || value instanceof Blob) {
  //         // Append file or blob without JSON.stringify
  //         formData.append(key, value);
  //       } else if (value != null) {
  //         // Append other values as strings
  //         formData.append(key, String(value));
  //       }
  //     });

  //     if (selectedFile) {
  //       formData.append("circleImage", selectedFile);
  //     }

  //     const response = await dispatch(
  //       updateCircle({ unionId: circleId, payload: formData })
  //     ).unwrap();

  //     if (response?.message) {
  //       setSuccessAlert(true);
  //       setUpdateSuccess(true);
  //     } else {
  //       setSuccessAlert(false);
  //       setErrorAlert("Unknown error occurred. Please try later");
  //     }
  //   } catch (error: any) {
  //     setSuccessAlert(false);
  //     setErrorAlert(
  //       error.message || "Unknown error occurred during circle update."
  //     );
  //   }
  // };

  const handleSubmit = async (values: IUpdateCircle) => {
    try {
      const formData = new FormData();

      // Append simple text fields.
      formData.append("circleName", values.circleName ?? "");
      formData.append("description", values.description ?? "");
      formData.append("leader", values.leader ?? "");
      formData.append("membersCount", values.membersCount ?? "");

      // Append members if any.
      if (values.members) {
        values.members.forEach((member) => formData.append("members", member));
      }

      // Append configuration fields.
      if (values.configuration) {
        formData.append(
          "configuration[contributionAmount]",
          String(values.configuration.contributionAmount)
        );
        formData.append(
          "configuration[maxContributionTime]",
          values.configuration.maxContributionTime ?? ""
        );
        formData.append(
          "configuration[contributionPeriod]",
          values.configuration.contributionPeriod
        );
        formData.append(
          "configuration[contributionFrequency]",
          String(values.configuration.contributionFrequency)
        );
        formData.append(
          "configuration[contributionDays]",
          JSON.stringify(values.configuration.contributionDays)
        );
        formData.append("configuration[payOutPeriod]", "Monthly");
        formData.append(
          "configuration[payOutDay]",
          values.configuration.payOutDay ?? ""
        );
      }

      // // Append payOut fields (singular).
      // if (values.payOut) {
      //   formData.append("payOut[payOutPeriod]", values.payOut.payOutPeriod);
      //   // Since the schema expects a string for payOutDay, we directly append the string.
      //   formData.append("payOut[payOutDay]", values.payOut.payOutDay);
      // }

      // Append file if one is selected.
      if (selectedFile) {
        formData.append("circleImage", selectedFile);
      }

      const response = await dispatch(
        updateCircle({ unionId: circleId, payload: formData })
      ).unwrap();

      if (response?.message) {
        setSuccessAlert(true);
        setUpdateSuccess(true);
      } else {
        setSuccessAlert(false);
        setErrorAlert("Unknown error occurred. Please try later");
      }
    } catch (error: any) {
      setSuccessAlert(false);
      setErrorAlert(
        error.message || "Unknown error occurred during circle update."
      );
    }
  };

  useEffect(() => {
    if (updateSuccess) {
      dispatch(fetchCircleByLeader(userData?._id ?? ""));
      setUpdateSuccess(false);
    }
  }, [updateSuccess, dispatch, userData?._id]);

  const handleCloseSuccessAlert = () => {
    setSuccessAlert(false);
  };

  const handleCloseErrorAlert = () => {
    setErrorAlert("");
  };

  return (
    <BootstrapDialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      {/* ── Header band ─────────────────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: colors.primary,
          px: { xs: 2.5, sm: 4 },
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: alpha(colors.mainSecondary, 0.15),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <SettingsIcon sx={{ color: colors.mainSecondary, fontSize: 22 }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: fonts.montserrat,
                fontWeight: 800,
                fontSize: { xs: "1rem", sm: "1.15rem" },
                color: "#fff",
                lineHeight: 1.2,
              }}
            >
              Update Your Circle
            </Typography>
            <Typography
              sx={{
                fontFamily: fonts.montserrat,
                fontSize: "0.72rem",
                color: alpha("#fff", 0.6),
                mt: 0.3,
              }}
            >
              Update config settings for your circle
            </Typography>
          </Box>
        </Stack>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ color: alpha("#fff", 0.7) }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 2, pb: 3, px: { xs: 2.5, sm: 4 } }}>
        {successAlert && (
          <Stack sx={{ width: "100%", mb: 2 }} spacing={2}>
            <Alert severity="success" onClose={handleCloseSuccessAlert}>
              Success — <strong>Circle Updated Successfully</strong>
            </Alert>
          </Stack>
        )}

        {errorAlert && (
          <Stack sx={{ width: "100%", mb: 2 }} spacing={2}>
            <Alert severity="error" onClose={handleCloseErrorAlert}>
              Error —{" "}
              <strong>
                {errorAlert || "Error occurred during circle update."}
              </strong>
            </Alert>
          </Stack>
        )}

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ mt: 1 }}>
          <Grid justifyContent="center" container spacing={5}>
            <Grid item xs={12} md={10} lg={9}>
              <CircleForm
                initialValues={initialValues}
                handleSelectFile={handleSelectFile}
                imagePreview={imagePreview}
                handleSubmit={handleSubmit}
                subTitle="Update Config Settings For Your Circle"
                title="Update Your Circle"
                isLoading={isLoading}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </BootstrapDialog>
  );
};

UpdateCircle.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  circleId: PropTypes.string,
};

export default UpdateCircle;
